import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { CalendarDays, ShoppingCart, Trash2, Printer, Save, Loader2, X } from 'lucide-react';
import { authFetch } from '../lib/authFetch';
import { toast } from 'sonner';
import { DIET_MEALS, MEAL_CATEGORIES } from '../data/dietMeals';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

const MEAL_BY_ID = Object.fromEntries(DIET_MEALS.map(m => [m.id, m]));

function emptyDay() {
  return { breakfast: null, lunch: null, dinner: null, snacks: null, smoothies: null };
}

// Parse "1 cup oats" → { qty: 1, unit: 'cup', item: 'oats' }
// Used to merge identical ingredients across meals.
function parseIngredient(text) {
  const t = (text || '').trim();
  // Handle leading fraction or number, optional unit
  const re = /^(\d+(?:\.\d+)?(?:\s*\/\s*\d+)?|\d+\s+\d+\/\d+|\d+\/\d+)?\s*([a-zA-Z]+)?\s+(.+)$/;
  const m = t.match(re);
  if (!m) return { qty: null, unit: '', item: t.toLowerCase() };
  const qtyRaw = m[1] || '';
  const unit = (m[2] || '').toLowerCase();
  const itemRaw = (m[3] || '').toLowerCase();
  // Parse mixed fraction or simple fraction
  let qty = null;
  if (qtyRaw) {
    if (qtyRaw.includes(' ')) {
      const [whole, frac] = qtyRaw.split(/\s+/);
      const [n, d] = frac.split('/').map(Number);
      qty = parseFloat(whole) + (n / d);
    } else if (qtyRaw.includes('/')) {
      const [n, d] = qtyRaw.split('/').map(Number);
      qty = n / d;
    } else {
      qty = parseFloat(qtyRaw);
    }
  }
  // Common units we'll normalise (singular form)
  const knownUnits = new Set(['cup', 'cups', 'tbsp', 'tsp', 'g', 'kg', 'ml', 'l', 'oz', 'lb', 'tin', 'tins', 'can', 'cans', 'slice', 'slices', 'cloves', 'clove']);
  const finalUnit = knownUnits.has(unit) ? unit.replace(/s$/, '') : '';
  const item = finalUnit ? itemRaw : (unit ? `${unit} ${itemRaw}` : itemRaw).trim();
  return { qty, unit: finalUnit, item: item.replace(/^,\s*/, '').replace(/\s+/g, ' ').trim(), original: text };
}

function buildShoppingList(plan) {
  const groups = {}; // key: unit|item → { qty, unit, item, count, examples: [] }
  Object.values(plan || {}).forEach(day => {
    Object.values(day || {}).forEach(mealId => {
      const meal = mealId && MEAL_BY_ID[mealId];
      if (!meal) return;
      meal.ingredients.forEach(ing => {
        const p = parseIngredient(ing);
        const key = `${p.unit}|${p.item}`;
        if (!groups[key]) {
          groups[key] = { qty: 0, unit: p.unit, item: p.item, count: 0, hasNumeric: false };
        }
        groups[key].count += 1;
        if (p.qty !== null && !isNaN(p.qty)) {
          groups[key].qty += p.qty;
          groups[key].hasNumeric = true;
        }
      });
    });
  });
  // Build display items
  const items = Object.values(groups).map(g => {
    let display;
    if (g.hasNumeric && g.unit) {
      const qtyStr = Number.isInteger(g.qty) ? g.qty.toString() : g.qty.toFixed(2).replace(/\.?0+$/, '');
      display = `${qtyStr} ${g.unit}${g.qty !== 1 ? 's' : ''} ${g.item}`;
    } else if (g.hasNumeric) {
      const qtyStr = Number.isInteger(g.qty) ? g.qty.toString() : g.qty.toFixed(2).replace(/\.?0+$/, '');
      display = `${qtyStr} ${g.item}`;
    } else {
      display = g.item;
    }
    return { text: display, count: g.count };
  });
  return items.sort((a, b) => a.text.localeCompare(b.text));
}

export default function MealPlanner({ onClose }) {
  const [plan, setPlan] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeView, setActiveView] = useState('plan'); // 'plan' | 'shopping'
  const [picker, setPicker] = useState(null); // { day, slot }

  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const loadPlan = useCallback(async () => {
    try {
      const res = await authFetch(`${apiBase}/api/user/meal-plan`);
      if (res.ok) {
        const data = await res.json();
        setPlan(data.plan || {});
      }
    } catch (e) {
      // Best-effort load — meal plan stays empty until next attempt.
      console.debug('[MealPlanner] loadPlan failed:', e?.message);
    }
    setLoading(false);
  }, [apiBase]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const savePlan = async (newPlan, opts = {}) => {
    setSaving(true);
    try {
      const res = await authFetch(`${apiBase}/api/user/meal-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: newPlan }),
      });
      if (res.ok) {
        if (!opts.silent) toast.success('Meal plan saved');
      } else {
        toast.error('Could not save plan');
      }
    } catch (e) {
      toast.error('Network error');
    }
    setSaving(false);
  };

  const setSlot = (day, slot, mealId) => {
    const next = { ...plan };
    if (!next[day]) next[day] = emptyDay();
    next[day] = { ...next[day], [slot]: mealId };
    setPlan(next);
    savePlan(next, { silent: true });
  };

  const clearSlot = (day, slot) => setSlot(day, slot, null);

  const clearWeek = () => {
    if (!window.confirm('Clear the entire weekly plan?')) return;
    setPlan({});
    savePlan({});
  };

  const shoppingList = useMemo(() => buildShoppingList(plan), [plan]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="meal-planner">
      {/* Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h3 className="text-sm font-bold text-blue-900 mb-1 flex items-center gap-2">
          <CalendarDays className="w-4 h-4" />
          Your Weekly Meal Plan
        </h3>
        <p className="text-xs text-blue-800 leading-relaxed">
          Plan your meals ahead: a daily Recovery tool. Pre-committed meals reduce decision-fatigue, stabilise blood-sugar and remove the unstructured time when cravings hit hardest.
        </p>
      </div>

      {/* View toggle */}
      <div className="flex gap-1 bg-white border border-zinc-200 rounded-xl p-1">
        <button
          onClick={() => setActiveView('plan')}
          className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
            activeView === 'plan' ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-50'
          }`}
          data-testid="planner-view-plan"
        >
          <CalendarDays className="w-3.5 h-3.5" />
          Plan
        </button>
        <button
          onClick={() => setActiveView('shopping')}
          className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
            activeView === 'shopping' ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-50'
          }`}
          data-testid="planner-view-shopping"
        >
          <ShoppingCart className="w-3.5 h-3.5" />
          Shopping List ({shoppingList.length})
        </button>
      </div>

      {activeView === 'plan' && (
        <div className="space-y-3" data-testid="planner-grid">
          {DAYS.map(day => {
            const dayPlan = plan[day] || emptyDay();
            return (
              <div key={day} className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200">
                  <span className="text-xs font-bold uppercase tracking-wider text-zinc-700">{day}</span>
                </div>
                <div className="divide-y divide-zinc-100">
                  {MEAL_CATEGORIES.map(cat => {
                    const mealId = dayPlan[cat.id];
                    const meal = mealId ? MEAL_BY_ID[mealId] : null;
                    return (
                      <div key={cat.id} className="px-4 py-2.5 flex items-center gap-3">
                        <span className="text-base flex-shrink-0">{cat.icon}</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 w-20 flex-shrink-0">{cat.label}</span>
                        {meal ? (
                          <>
                            <span className="text-sm text-zinc-800 flex-1 truncate">{meal.title}</span>
                            <button
                              onClick={() => clearSlot(day, cat.id)}
                              className="text-zinc-400 hover:text-red-600 p-1"
                              data-testid={`clear-${day}-${cat.id}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setPicker({ day, slot: cat.id })}
                            className="text-xs text-blue-600 hover:underline flex-1 text-left"
                            data-testid={`add-${day}-${cat.id}`}
                          >
                            + Add meal
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="flex justify-between gap-2">
            <button
              onClick={clearWeek}
              className="flex items-center gap-1.5 text-xs text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg"
              data-testid="planner-clear-week"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear week
            </button>
            <span className="text-xs text-zinc-400 self-center">
              {saving ? 'Saving…' : 'Auto-saved'}
            </span>
          </div>
        </div>
      )}

      {activeView === 'shopping' && (
        <div className="space-y-3" data-testid="shopping-list">
          {shoppingList.length === 0 ? (
            <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center">
              <ShoppingCart className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
              <p className="text-sm text-zinc-500">Add meals to your plan to generate a shopping list.</p>
            </div>
          ) : (
            <>
              <div className="bg-white border border-zinc-200 rounded-xl p-4 print:border-none print:p-0">
                <h3 className="text-base font-bold text-zinc-900 mb-3 print:text-xl">Shopping list</h3>
                <ul className="space-y-1.5">
                  {shoppingList.map((item, i) => (
                    <li key={i} className="text-sm text-zinc-700 flex items-start gap-2">
                      <input type="checkbox" className="mt-1 flex-shrink-0" />
                      <span className="flex-1">
                        {item.text}
                        {item.count > 1 && (
                          <span className="text-xs text-zinc-400 ml-2">×{item.count} meals</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={handlePrint}
                className="w-full flex items-center justify-center gap-2 bg-zinc-900 text-white rounded-xl py-3 text-sm font-semibold hover:bg-zinc-800 transition-colors print:hidden"
                data-testid="print-shopping-list"
              >
                <Printer className="w-4 h-4" />
                Print shopping list
              </button>
              <p className="text-[11px] text-zinc-400 text-center print:hidden">
                Items grouped by raw ingredient. Adjust quantities to your household size when you shop.
              </p>
            </>
          )}
        </div>
      )}

      {/* Meal picker modal */}
      {picker && (
        <MealPickerModal
          slot={picker.slot}
          onSelect={(mealId) => { setSlot(picker.day, picker.slot, mealId); setPicker(null); }}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}

function MealPickerModal({ slot, onSelect, onClose }) {
  const meals = DIET_MEALS.filter(m => m.category === slot);
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        className="bg-white w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
          <h3 className="text-sm font-bold capitalize">Choose a {slot}</h3>
          <button onClick={onClose} className="p-1 text-zinc-500"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 grid grid-cols-2 gap-2">
          {meals.map(m => (
            <button
              key={m.id}
              onClick={() => onSelect(m.id)}
              className="text-left bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 rounded-lg p-3"
              data-testid={`picker-${m.id}`}
            >
              <div className="text-sm font-semibold text-zinc-800 leading-tight">{m.title}</div>
              <div className="text-[10px] text-zinc-500 mt-1">{m.prep_minutes + m.cook_minutes} min · {m.difficulty}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
